import { Typography, useTheme, useMediaQuery } from "@mui/material";
import { observer } from "mobx-react";
import React from "react";
import { Grid } from "@mui/material";
import { parseFromISO, standardDateTimeFormat } from "utils/DateTimeUtils";
import ClassifierCategory from "models/ClassifierCategoryId";
import ClassifierGroup from "models/ClassifierGroup";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { CategorySelector } from "components/Classifiers/Common/CategorySelector";
import { GroupSelector } from "components/Classifiers/Common/GroupSelector";
import { TitledSection } from "components/Classifiers/Common/TitledSection";

interface Props {
    name: string;
    onNameChange: (e: any) => void;
    description: string;
    onDescriptionChange: (e: any) => void;
    category: ClassifierCategory | null;
    onCategoryChange: (e: any) => void;
    group?: ClassifierGroup;
    onGroupChange: (group?: ClassifierGroup) => void;
    modifiedOn?: string;
    isSmartPredictActive: boolean;
}

const ClassifierBuilderDetails = observer(
    ({
        name,
        onNameChange,
        description,
        onDescriptionChange,
        category,
        onCategoryChange,
        group,
        onGroupChange,
        modifiedOn,
        isSmartPredictActive,
    }: Props) => {
        const theme = useTheme();
        const isLargeUp = useMediaQuery(theme.breakpoints.up("lg"));
        return (
            <>
                <TitledSection
                    title={
                        <Grid
                            container
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="nowrap"
                        >
                            <Grid item>Classifier Details</Grid>
                            {modifiedOn && (
                                <Grid item>
                                    <Typography
                                        sx={{
                                            color: "#9CA3AF",
                                            fontSize: "14px",
                                            fontWeight: 400,
                                        }}
                                    >
                                        {`Last Updated: ${standardDateTimeFormat(
                                            parseFromISO(modifiedOn, false),
                                        )}` +
                                            (isSmartPredictActive ? " | " : "")}
                                        {isSmartPredictActive && (
                                            <span
                                                style={{
                                                    color: theme.palette.green
                                                        .main,
                                                    fontSize: "14px",
                                                    fontWeight: 400,
                                                }}
                                            >
                                                Active SmartPredict
                                            </span>
                                        )}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    }
                >
                    <Grid
                        container
                        sx={{
                            paddingBottom: theme.spacing(2.5),
                            display: "grid",
                            gridTemplateColumns: isLargeUp
                                ? "repeat(6, 1fr)"
                                : "repeat(6, 1fr)",
                            gridTemplateRows: isLargeUp
                                ? "repeat(2, 1fr)"
                                : "repeat(3, 1fr)",
                            gap: isLargeUp ? 2 : "16px",
                            gridColumnGap: isLargeUp ? undefined : "16px",
                            gridRowGap: isLargeUp ? undefined : "16px",
                        }}
                    >
                        <Grid
                            item
                            sx={{
                                paddingBottom: theme.spacing(2.5),
                                gridArea: isLargeUp ? "1/1/2/5" : "1/1/2/7",
                            }}
                        >
                            <AcxMainTextField
                                id="classifier-name"
                                labelText="Classifier Name"
                                fullWidth
                                required
                                value={name}
                                onChange={onNameChange}
                            />
                        </Grid>
                        <Grid
                            item
                            sx={{
                                paddingBottom: theme.spacing(2.5),
                                gridArea: isLargeUp ? "1/5/2/6" : "2/1/3/4",
                            }}
                        >
                            <CategorySelector
                                id="classifier-category"
                                inputLabel="Classifier Category"
                                fullWidth
                                defaultValue={category}
                                onChange={onCategoryChange}
                            />
                        </Grid>
                        <Grid
                            item
                            sx={{
                                paddingBottom: theme.spacing(2.5),
                                gridArea: isLargeUp ? "1/6/2/7" : "2/4/3/7",
                            }}
                        >
                            <GroupSelector
                                id="classifier-group"
                                inputLabel="Classifier Group"
                                maxDepth={1}
                                fullWidth
                                defaultValue={group}
                                onChange={onGroupChange}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sx={{ gridArea: isLargeUp ? "2/1/3/7" : "3/1/4/7" }}
                        >
                            <AcxMainTextField
                                id="classifier-description"
                                labelText="Description"
                                fullWidth
                                value={description}
                                onChange={onDescriptionChange}
                            />
                        </Grid>
                    </Grid>
                </TitledSection>
            </>
        );
    },
);

export default ClassifierBuilderDetails;
